import clsx from 'clsx';
import type { ToolCallState } from '@/hooks/use-streaming-v2';
import { formatNumber } from '@/lib/format';
import { PhaseShell, PlaceholderRows } from './ToolResultRendererSkeleton';

interface SensitivityRow {
  inputValue: number;
  inputFormatted: string;
  outputs: Record<string, number>;
  isBase: boolean;
}

interface SensitivityData {
  test: {
    input: string;
    inputDisplayName: string;
    outputs: Array<{ name: string; displayName: string }>;
    baseValue: number;
    method: string;
    stepSize: number;
    steps: number;
  };
  results: SensitivityRow[];
  elasticity?: Record<string, number>;
}

function isSensitivityResult(data: unknown): data is SensitivityData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'test' in data &&
    'results' in data &&
    Array.isArray((data as SensitivityData).results)
  );
}

function getBaseOutputs(results: SensitivityRow[]): Record<string, number> {
  const baseRow = results.find((r) => r.isBase);
  return baseRow?.outputs ?? {};
}

function renderComplete(result: SensitivityData) {
  const { test, results } = result;
  const baseOutputs = getBaseOutputs(results);
  const outputNames = test.outputs;
  return (
    <div className="space-y-2">
      <div className="text-xs font-mono font-semibold text-accent tracking-[1.2px] uppercase">
        Sensitivity: {test.inputDisplayName}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-panel-border">
              <th className="text-left text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                {test.inputDisplayName}
              </th>
              {outputNames.map((o) => (
                <th key={o.name} className="text-right text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                  {o.displayName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results.map((row, i) => (
              <tr
                key={i}
                className={clsx(
                  'border-b border-panel-border/50 transition-colors hover:bg-accent-faint',
                  row.isBase && 'border-l-2 border-l-accent bg-accent-faint',
                )}
              >
                <td className={clsx(
                  'text-sm font-mono px-2 py-1 tabular-nums',
                  row.isBase ? 'text-accent font-semibold' : 'text-text',
                )}>
                  {row.inputFormatted}
                  {row.isBase && <span className="text-xs text-accent ml-1">(base)</span>}
                </td>
                {outputNames.map((o) => {
                  const value = row.outputs[o.name];
                  const baseValue = baseOutputs[o.name];
                  const isBetter = typeof value === 'number' && typeof baseValue === 'number' && value > baseValue;
                  const isWorse = typeof value === 'number' && typeof baseValue === 'number' && value < baseValue;
                  return (
                    <td
                      key={o.name}
                      className={clsx(
                        'text-right text-sm font-mono px-2 py-1 tabular-nums',
                        row.isBase && 'text-text-bright font-semibold',
                        !row.isBase && isBetter && 'text-accent',
                        !row.isBase && isWorse && 'text-danger',
                        !row.isBase && !isBetter && !isWorse && 'text-text',
                      )}
                    >
                      {typeof value === 'number' ? formatNumber(value, 4) : '-'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result.elasticity && Object.keys(result.elasticity).length > 0 && (
        <div className="flex gap-3 mt-1">
          {Object.entries(result.elasticity).map(([key, val]) => (
            <div key={key} className="text-xs font-mono text-text-muted">
              Elasticity ({key}): <span className="text-text-bright tabular-nums">{formatNumber(val, 3)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface SensitivityTableProps {
  state: ToolCallState;
}

/**
 * Phase 4 Session 2 — progressive row paint from `step_computed` events.
 *
 * `state.progress` carries `steps_planned` (count) + N × `step_computed`
 * (with `x` + `output: Record<name, number>`). Rows are rendered ordered by
 * `x` value (which corresponds to row order — the agent emits in sweep order).
 * Un-painted rows render as dim placeholders.
 */
function renderInProgress(state: Extract<ToolCallState, { phase: 'executing' }>) {
  const input = state.input as {
    input_name?: string;
    inputName?: string;
    outputNames?: string[];
    steps?: number;
  };
  const inputName = input.input_name ?? input.inputName ?? '…';
  const outputNames = Array.isArray(input.outputNames) ? input.outputNames : [];

  const plannedEvent = state.progress.find((p) => p.phase === 'steps_planned');
  const plannedCount = (plannedEvent?.payload as { count?: number } | undefined)?.count;
  const expectedRows =
    typeof plannedCount === 'number' && plannedCount > 0
      ? plannedCount
      : (input.steps ?? 5) * 2 + 1;

  const stepEvents = state.progress.filter((p) => p.phase === 'step_computed');

  return (
    <PhaseShell variant="in-progress" title={`Sensitivity: ${inputName}`}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" data-testid="sensitivity-1d-progress">
          <thead>
            <tr className="border-b border-panel-border">
              <th className="text-left text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                {inputName}
              </th>
              {outputNames.map((o) => (
                <th
                  key={o}
                  className="text-right text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1"
                >
                  {o}
                </th>
              ))}
              {outputNames.length === 0 && (
                <th className="text-right text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                  Output
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: expectedRows }).map((_, i) => {
              const event = stepEvents[i];
              if (!event) {
                return (
                  <tr
                    key={i}
                    data-row-painted="false"
                    className="border-b border-panel-border/50"
                  >
                    <td className="text-sm font-mono text-text-dim px-2 py-1">—</td>
                    {(outputNames.length > 0 ? outputNames : ['placeholder']).map((o) => (
                      <td
                        key={o}
                        className="text-right text-sm font-mono text-text-dim px-2 py-1"
                      >
                        —
                      </td>
                    ))}
                  </tr>
                );
              }
              const payload = event.payload as { x?: number; output?: Record<string, unknown> };
              const x = typeof payload.x === 'number' ? payload.x : 0;
              const out = (payload.output ?? {}) as Record<string, unknown>;
              const cols = outputNames.length > 0 ? outputNames : Object.keys(out);
              return (
                <tr
                  key={i}
                  data-row-painted="true"
                  className="border-b border-panel-border/50"
                >
                  <td className="text-sm font-mono text-text px-2 py-1 tabular-nums">
                    {formatNumber(x, 4)}
                  </td>
                  {cols.map((o) => {
                    const v = out[o];
                    return (
                      <td
                        key={o}
                        className="text-right text-sm font-mono text-text px-2 py-1 tabular-nums"
                      >
                        {typeof v === 'number' ? formatNumber(v, 4) : '—'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PhaseShell>
  );
}

/**
 * Phase-aware SensitivityTable. Per directive §7.2:
 * - skeleton: header row with axis label populated as `partialInput.input_name`
 *   streams; empty rows = `partialInput.steps ?? 5` placeholder rows.
 * - in-progress: rows fill in as Phase 4's `step_computed` events arrive.
 * - complete: existing render.
 * - aborted: rows already painted stay; remaining placeholders dim out.
 */
export function SensitivityTable({ state }: SensitivityTableProps) {
  if (state.phase === 'complete' && isSensitivityResult(state.result)) {
    return renderComplete(state.result);
  }
  if (state.phase === 'complete') {
    // Result didn't match expected shape — fallback render.
    return (
      <div className="text-xs font-mono text-text-muted">
        <pre className="whitespace-pre-wrap">{JSON.stringify(state.result, null, 2)}</pre>
      </div>
    );
  }

  if (state.phase === 'executing') {
    return renderInProgress(state);
  }

  // Probe partialInput / input for axis label + step count.
  const inputData =
    state.phase === 'input_streaming' ? state.partialInput :
    state.phase === 'input_ready' ? state.input :
    {};
  const inputName = (inputData as { input_name?: string; input?: string }).input_name
    ?? (inputData as { input?: string }).input
    ?? '...';
  const steps =
    typeof (inputData as { steps?: number }).steps === 'number'
      ? (inputData as { steps: number }).steps
      : 5;

  const variant = state.phase === 'aborted' ? 'aborted' : 'skeleton';

  return (
    <PhaseShell variant={variant} title={`Sensitivity: ${inputName}`}>
      <PlaceholderRows count={steps} columns={2} />
    </PhaseShell>
  );
}
